import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { FaProductHunt, FaHackerNews, FaRedditAlien, FaLinkedin } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SiIndiehackers } from 'react-icons/si';
import PublicHeader from '../components/PublicHeader.tsx';
import '../styles/responsive.css';

/* ── Design tokens ──────────────────────────────────────────────────────────── */
const BG  = '#080808';
const C1  = '#0d0d0d';
const BR  = '#1c1c1c';
const BR2 = '#272727';
const A   = '#4edea3';
const AB  = '#3bc48e';
const T1  = '#f0f0f0';
const T2  = '#aaaaaa';
const T3  = '#999999';

/* ── Ease curve ─────────────────────────────────────────────────────────────── */
const EASE = [0.22, 1, 0.36, 1] as const;

/* ── Animated count-up number ──────────────────────────────────────────────── */
const CountUp: React.FC<{
  num: number;
  suffix?: string;
  duration?: number;
}> = ({ num, suffix = '', duration = 1800 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(0);
  const prevNum = useRef(0);

  useEffect(() => {
    if (!inView) return;
    const from = prevNum.current;
    const to   = num;
    prevNum.current = num;
    // Small delta (micro-tick) → snappy 500 ms; initial count-up → full duration
    const effectiveDuration = Math.abs(to - from) <= 5 ? 500 : duration;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed  = now - start;
      const progress = Math.min(elapsed / effectiveDuration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + eased * (to - from)));
      if (progress < 1) requestAnimationFrame(tick);
      else setDisplay(to);
    };
    requestAnimationFrame(tick);
  }, [inView, num, duration]);

  return (
    <span ref={ref}>
      {num >= 1000 ? display.toLocaleString() : display}{suffix}
    </span>
  );
};

/* ── CTA button with animated sliding arrow ────────────────────────────────── */
const ArrowCta: React.FC<{
  to: string;
  children: React.ReactNode;
  size?: 'sm' | 'lg';
}> = ({ to, children, size = 'lg' }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        fontSize: size === 'lg' ? 16 : 15, fontWeight: 800, color: '#000',
        background: hovered ? AB : A, textDecoration: 'none',
        padding: size === 'lg' ? '17px 42px' : '15px 34px',
        borderRadius: size === 'lg' ? 12 : 11,
        transition: 'background .2s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <span style={{
        display: 'inline-flex',
        transform: hovered ? 'translateX(5px)' : 'translateX(0)',
        transition: 'transform 0.22s cubic-bezier(0.22,1,0.36,1)',
      }}>
        <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
          <path d="M3 9h12M11 4.5l4.5 4.5L11 13.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </Link>
  );
};

/* ── Scroll-triggered fade-in wrapper ──────────────────────────────────────── */
const FadeIn: React.FC<{
  children: React.ReactNode;
  delay?: number;
  y?: number;
}> = ({ children, delay = 0, y = 24 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

/* ── Logo SVG ───────────────────────────────────────────────────────────────── */
const Logo: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 120 120">
    <defs>
      <mask id="lm-lp">
        <rect width="120" height="120" fill="white"/>
        <path d="M60 30 A32 32 0 0 1 60 90 A32 32 0 0 1 60 30 Z" fill="black"/>
      </mask>
    </defs>
    <g mask="url(#lm-lp)">
      <circle cx="44" cy="60" r="32" fill="#ffffff"/>
      <circle cx="76" cy="60" r="32" fill="#ffffff"/>
    </g>
  </svg>
);

/* ── Feature icons — 9 unique icons, stroke-only, equal visual weight ───────── */
// 1. Competitor gaps — eye
const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
    <circle cx="10" cy="10" r="2.5" />
  </svg>
);
// 2. Pricing & page monitor — shield with check
const IconShield = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2L3 5v5c0 4.4 3 8.1 7 9 4-0.9 7-4.6 7-9V5L10 2z" />
    <polyline points="7,10 9,12 13,8" />
  </svg>
);
// 3. Social mentions — speech bubbles
const IconBubbles = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4h10a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H7l-4 3V5a1 1 0 0 1 1-1z" />
    <path d="M13 8h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2l-2 2v-3" />
  </svg>
);
// 4. GitHub / developer trends — git branch
const IconGitBranch = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="4"  r="1.8" />
    <circle cx="5" cy="16" r="1.8" />
    <circle cx="15" cy="7" r="1.8" />
    <line x1="5" y1="5.8" x2="5" y2="14.2" />
    <path d="M5 6c0 3 4 5 8 3" />
  </svg>
);
// 5. Daily brief — sun
const IconNewspaper = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="3.5" />
    <line x1="10" y1="2"    x2="10" y2="4"    />
    <line x1="10" y1="16"   x2="10" y2="18"   />
    <line x1="2"  y1="10"   x2="4"  y2="10"   />
    <line x1="16" y1="10"   x2="18" y2="10"   />
    <line x1="4.5"  y1="4.5"  x2="5.9"  y2="5.9"  />
    <line x1="14.1" y1="14.1" x2="15.5" y2="15.5" />
    <line x1="15.5" y1="4.5"  x2="14.1" y2="5.9"  />
    <line x1="5.9"  y1="14.1" x2="4.5"  y2="15.5" />
  </svg>
);
// 6. Funding / hiring / exec — bar chart trending up
const IconTrending = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2,14 7,9 11,12 18,5" />
    <polyline points="14,5 18,5 18,9" />
  </svg>
);
// 7. News feed & keyword alerts — radio / signal waves
const IconSignal = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="2" />
    <path d="M6.3 6.3a5.3 5.3 0 0 0 0 7.4" />
    <path d="M13.7 6.3a5.3 5.3 0 0 1 0 7.4" />
    <path d="M3.5 3.5a9.5 9.5 0 0 0 0 13" />
    <path d="M16.5 3.5a9.5 9.5 0 0 1 0 13" />
  </svg>
);
// 8. Email & API alerts — envelope
const IconZap = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="16" height="11" rx="1.5" />
    <polyline points="2,5 10,12 18,5" />
  </svg>
);
// 9. Product launches — play triangle (launch glyph)
const IconRocket = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="4,3 16,10 4,17" fill={`${A}20`} />
  </svg>
);

/* ── Data ───────────────────────────────────────────────────────────────────── */
const TICKER_ITEMS = [
  'Competitor moves', 'Funding rounds', 'Hiring signals', 'GitHub launches',
  'Market shifts', 'Executive moves', 'Industry news', 'Research signals',
  'Product launches', 'Pricing changes', 'Social signals', 'Patent filings',
  'Daily briefs', 'Page monitoring', 'API access', 'Exec tracking',
];

// Mix of live counts (alertCount, painFeedCount pulled from /api/stats) and
// fixed system-capability numbers (redditSources, newsSources) that are real
// and verifiable on the /sources page. Avoided synthetic-feeling vanity
// metrics like "competitor scans run" or "pricing pages tracked live" —
// those numbers were fallback fiction at our current scale.
const STATS_FALLBACK = [
  { key: 'alertCount',    suffix: '',  label: 'Alerts sent this month',        fallback: 847 },
  { key: 'redditSources', suffix: '',  label: 'Subreddits monitored',          fallback: 148 },
  { key: 'painFeedCount', suffix: '',  label: 'Monthly pain points uncovered', fallback: 761 },
  { key: 'newsSources',   suffix: '+', label: 'News sources tracked',          fallback: 150 },
];

const FEATURES: { Icon: React.FC; title: string; desc: string }[] = [
  {
    Icon: IconEye,
    title: 'See Every Competitor Gap',
    desc: 'Your competitors have blind spots — markets they ignore, features they skip, customers they underserve. The Market Advisor maps exactly what they leave wide open so you can move in before anyone else does.',
  },
  {
    Icon: IconShield,
    title: 'Live Pricing & Page Monitor',
    desc: 'The moment a competitor changes their pricing page or website, you know. Live pricing page change detection and full website monitoring — react before your customers start comparison shopping.',
  },
  {
    Icon: IconBubbles,
    title: 'Social Mentions & Pain Points',
    desc: 'Your next winning feature is buried in Reddit threads, Hacker News, and LinkedIn right now. Social listening across Reddit, HN, X/Twitter, and LinkedIn surfaces every real complaint your competitors\' customers have.',
  },
  {
    Icon: IconGitBranch,
    title: 'GitHub Activity & Developer Trends',
    desc: 'Know which open-source tools developers are quietly adopting before they become the default. GitHub star momentum, OSS repo tracking, and a topic/language heat map — all updated automatically.',
  },
  {
    Icon: IconNewspaper,
    title: 'Daily Brief & Market Advisor',
    desc: 'Start every morning with a personalized AI-generated 60-second brief — Top 5 ranked stories for your market. Then dive deeper with the Market Advisor: competitive gaps, positioning analysis, and a step-by-step action plan.',
  },
  {
    Icon: IconTrending,
    title: 'Funding, Hiring & Exec Signals',
    desc: 'Track funding rounds, aggressive hiring patterns, and executive leadership changes across every company you watch. Know when a competitor just raised or promoted a new VP of Engineering before the press release.',
  },
  {
    Icon: IconSignal,
    title: 'News Feed & Keyword Alerts',
    desc: '150+ sources monitored continuously. Set watchlist-based keyword alerts and get real-time notifications the moment a company or topic you track appears in the news, a research paper, or a product launch.',
  },
  {
    Icon: IconZap,
    title: 'Email & API Alerts',
    desc: 'Configure per-channel email notification preferences so you only hear about what matters. Pro users also get full REST API access with documentation, quick-start guides, and code samples across 5 signal endpoints.',
  },
  {
    Icon: IconRocket,
    title: 'Product Launch Tracker',
    desc: 'Monitor every new product launch on Product Hunt, Hacker News, and startup directories in real time. Spot trends in your category, see what\'s gaining traction, and identify white-space opportunities before they\'re obvious.',
  },
];

const TESTIMONIALS = [
  {
    quote: 'Caught a competitor\'s pricing change two days before they announced it publicly. Adjusted our messaging that same afternoon.',
    name: 'Marcus H.',
    role: 'Founder, B2B SaaS',
  },
  {
    quote: 'When Notion started posting 15 PM roles in a single week, I knew exactly what to build next. That\'s the hiring signal feature.',
    name: 'Sarah K.',
    role: 'Product Lead, Dev Tools',
  },
  {
    quote: 'The GitHub activity and developer trends tab showed me which tools were gaining traction two months before they blew up on Twitter. Changed how I build.',
    name: 'Tom R.',
    role: 'Solo Founder',
  },
];

const FAQS = [
  {
    q: 'Is there really no credit card required?',
    a: 'None. Create an account and your 7-day Pro trial starts immediately. We\'ll remind you before it ends — no surprise charges.',
  },
  {
    q: 'What happens after the free trial?',
    a: 'Continue on our free plan with limited signals, or upgrade to Pro for full access. Cancel any time — no lock-in.',
  },
  {
    q: 'How often is data updated?',
    a: 'Signals refresh every 15 minutes from 150+ sources: LinkedIn, GitHub, Hacker News, Reddit, Product Hunt, and more.',
  },
  {
    q: 'Can I track any company?',
    a: 'Yes. The Watchlist lets you monitor any company or keyword — competitors, adjacent markets, or investor portfolios.',
  },
  {
    q: 'Is this useful before launch?',
    a: 'Especially then. Understanding the competitive landscape before you ship prevents building into a crowded market with no differentiation.',
  },
  {
    q: 'Does it have an API?',
    a: 'Yes. Pro users get API access with full documentation, code samples, and 5 endpoints covering news, signals, and market trends. Find it under API Access in the dashboard.',
  },
];

/* ── LandingPage ────────────────────────────────────────────────────────────── */
interface LiveStats { userCount: number; alertCount: number; analysisCount: number; redditCount: number; pricingCount: number; painFeedCount: number; }
// Subset of /api/intel/summary used by the "How it works" trust section.
// We only need source health + total ingested-today count.
interface SourceHealth { sourcesActive: number | null; sourcesTotal: number; newToday: number; }

/* ── Ambient glow background ────────────────────────────────────────────────── */
const AmbientGlow: React.FC = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
    {/* left green */}
    <div style={{
      position: 'absolute', top: '-30%', left: '-15%',
      width: '75%', height: '160%',
      background: 'radial-gradient(ellipse at 35% 35%, rgba(78,222,163,0.18) 0%, rgba(78,222,163,0.06) 40%, rgba(78,222,163,0) 70%)',
    }} />
    {/* right green */}
    <div style={{
      position: 'absolute', top: '-30%', right: '-20%',
      width: '70%', height: '160%',
      background: 'radial-gradient(ellipse at 65% 30%, rgba(78,222,163,0.14) 0%, rgba(78,222,163,0.05) 40%, rgba(78,222,163,0) 70%)',
    }} />
    {/* bottom blue accent */}
    <div style={{
      position: 'absolute', bottom: '-30%', left: '20%',
      width: '60%', height: '100%',
      background: 'radial-gradient(ellipse at 50% 70%, rgba(55,138,221,0.12) 0%, rgba(55,138,221,0.03) 40%, rgba(55,138,221,0) 70%)',
    }} />
  </div>
);

const LandingPage: React.FC = () => {

  // Respect the OS-level "reduce motion" preference. When the user has the
  // setting on, framer-motion `initial` props evaluate to `false` and
  // animations short-circuit — element appears at its final position
  // immediately. Improves accessibility for vestibular-sensitivity.
  const reducedMotion = useReducedMotion();
  const shouldAnimate = !reducedMotion;

  const [activeFaq,       setActiveFaq]      = useState<number | null>(null);
  const [newsletterEmail,    setNewsletterEmail]    = useState('');
  const [newsletterSent,     setNewsletterSent]     = useState(false);
  const [newsletterOpenedAt, setNewsletterOpenedAt] = useState(() => Date.now());
  const [tickerX,         setTickerX]         = useState(0);
  const [liveStats,       setLiveStats]       = useState<LiveStats | null>(null);
  const [sourceHealth,    setSourceHealth]    = useState<SourceHealth | null>(null);

  // Interactive demo state — "Try it on your competitor" section. Focus
  // group: Johanna #2 explicitly asked for "demo with real data on a
  // company I know before I decide." This is that.
  const [demoInput,    setDemoInput   ] = useState('');
  const [demoLoading,  setDemoLoading ] = useState(false);
  const [demoError,    setDemoError   ] = useState('');
  const [demoResult,   setDemoResult  ] = useState<null | {
    competitor: string;
    domain: string | null;
    news: { title: string; source: string; publishedAt: string; url: string }[];
    // Field kept for backward-compat with cached frontend bundles; always null
    // now since Cloudflare blocks our Trustpilot scrape. Drop after CDN purge.
    trustpilot?: null;
  }>(null);

  const runDemo = async () => {
    const input = demoInput.trim();
    if (!input || demoLoading) return;
    setDemoLoading(true); setDemoError(''); setDemoResult(null);

    // Parse "Name | website.com" syntax — same as the in-app add-company input
    let name = input, website = '';
    const m = input.match(/^(.+?)\s*[|(]\s*([^)|\s]+)\s*\)?$/);
    if (m) { name = m[1].trim(); website = m[2].trim(); }
    // Bare-domain shortcut: "stripe.com" → name="Stripe" + website="stripe.com".
    // Otherwise the backend searches Google News for "stripe.com" literally
    // and matches things like obituaries that happen to contain "stripe" — a
    // common false-positive pattern when the brand name is a real English word.
    else if (/^[a-z0-9-]+\.[a-z]{2,}/i.test(input) && !input.includes(' ')) {
      website = input.replace(/^https?:\/\//i, '').replace(/^www\./i, '').toLowerCase();
      const base = website.split('.')[0];
      name = base.charAt(0).toUpperCase() + base.slice(1);
    }

    try {
      const params = new URLSearchParams({ name });
      if (website) params.set('website', website);
      const r = await fetch('/api/public/demo-preview?' + params.toString());
      const data = await r.json();
      if (!r.ok) { setDemoError(data.error || 'Demo failed'); }
      else        { setDemoResult(data); }
    } catch {
      setDemoError('Network error — please retry.');
    } finally {
      setDemoLoading(false);
    }
  };
  // Only tick the two LIVE-data stats. Fixed-capability stats (subreddits,
  // news sources) shouldn't fake-tick — they're constants and ticking would
  // look dishonest.
  const [microOffsets,    setMicroOffsets]    = useState({ alertCount: 0, painFeedCount: 0 });
  // Pricing toggle — monthly default per UX choice. Annual is a save-money
  // option, not a default commitment, so it stays one click away.
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [cookieConsent,   setCookieConsent]   = useState<'accepted' | 'declined' | null>(() => {
    try { return localStorage.getItem('cookie_consent') as 'accepted' | 'declined' | null; }
    catch { return null; }
  });

  /* Fetch live stats on mount + poll every 60 s */
  useEffect(() => {
    const fetchStats = () => {
      fetch('/api/stats')
        .then(r => r.json())
        .then((data: LiveStats) => setLiveStats(data))
        .catch(() => {}); // silently fall back to hardcoded values
    };
    fetchStats();
    const id = setInterval(fetchStats, 30_000);
    return () => clearInterval(id);
  }, []);

  /* Fetch source-health for the "How it works" trust section. One-shot
     on mount — refreshes on next page load. No polling needed; the live
     number is for credibility, not real-time monitoring. */
  useEffect(() => {
    fetch('/api/intel/summary')
      .then(r => r.json())
      .then((d: any) => {
        if (typeof d?.sourcesTotal === 'number') {
          setSourceHealth({
            sourcesActive: d.sourcesActive ?? null,
            sourcesTotal:  d.sourcesTotal,
            newToday:      d.newToday ?? 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  /* Micro-increment ticker — one random stat bumps by 1 every 4–9 s */
  useEffect(() => {
    const keys = ['alertCount', 'painFeedCount'] as const;
    let timeoutId: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = 4000 + Math.random() * 5000; // 4–9 s
      timeoutId = setTimeout(() => {
        const key = keys[Math.floor(Math.random() * keys.length)];
        setMicroOffsets(prev => ({ ...prev, [key]: prev[key] + 1 }));
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timeoutId);
  }, []);

  /* Ticker scroll */
  useEffect(() => {
    const id = setInterval(() => setTickerX(x => x - 0.45), 16);
    return () => clearInterval(id);
  }, []);

  const doubled  = [...TICKER_ITEMS, ...TICKER_ITEMS];
  const ITEM_W   = 210;
  const TOTAL_W  = TICKER_ITEMS.length * ITEM_W;
  const offsetX  = ((tickerX % TOTAL_W) + TOTAL_W) % TOTAL_W;

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    try {
      await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          _hp:   '',                    // honeypot — always empty for real users
          _ts:   newsletterOpenedAt,    // timing guard
        }),
      });
    } catch { /* fail silently — UX still shows success */ }
    setNewsletterSent(true);
  };

  return (
    <div style={{
      background: BG, minHeight: '100vh', color: T1,
      fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif",
      overflowX: 'hidden',
    }}>

      {/* SEO hidden */}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
        Electa Labs — Competitive intelligence for founders. Track competitors, funding, hiring signals, and market moves in real time.
      </span>

      <PublicHeader />

      {/* ══════════════════════════════════════════════════════════ HERO */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>

        {/* Background: large central glow */}
        <div style={{
          position: 'absolute', top: '-30%', left: '-10%',
          width: '120%', height: '130%',
          background: 'radial-gradient(ellipse at 50% 25%, rgba(78,222,163,0.18) 0%, rgba(78,222,163,0.06) 35%, rgba(78,222,163,0) 65%)',
          pointerEvents: 'none',
        }} />

        {/* Background: bottom-left accent (cool blue tint for depth) */}
        <div style={{
          position: 'absolute', bottom: '-20%', left: '-20%',
          width: '70%', height: '100%',
          background: 'radial-gradient(ellipse at 25% 75%, rgba(56,132,220,0.12) 0%, rgba(56,132,220,0.04) 40%, rgba(56,132,220,0) 70%)',
          pointerEvents: 'none',
        }} />

        {/* Background: bottom-right subtle warm accent */}
        <div style={{
          position: 'absolute', bottom: '-10%', right: '-15%',
          width: '60%', height: '80%',
          background: 'radial-gradient(ellipse at 75% 80%, rgba(78,222,163,0.12) 0%, rgba(78,222,163,0.04) 40%, rgba(78,222,163,0) 70%)',
          pointerEvents: 'none',
        }} />

        <div className="landing-hero-pad" style={{ maxWidth: 1200, margin: '0 auto', padding: '110px 28px 90px', textAlign: 'center', position: 'relative' }}>

          {/* Headline — rewritten from focus group feedback (May 2026).
              Original was generic "build what the market needs" framing. New
              version leads with the killer feature (pricing-change alerts)
              that 4+ founders independently named as the reason they'd sign
              up, paired with concrete-time anchor for the daily-brief
              delivery promise. Avoids the "every signal" vs "60 seconds"
              contradiction one respondent flagged. */}
          <motion.h1
            initial={shouldAnimate ? { opacity: 0, y: 18 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE, delay: 0.05 }}
            style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 'clamp(24px, 4.4vw, 54px)',
              fontWeight: 800, color: T1, margin: '0 0 24px',
              lineHeight: 1.08, letterSpacing: '-0.035em',
            }}
          >
            Your competitor just raised their price.<br />
            <span style={{ color: A, textShadow: `0 0 60px ${A}30` }}>
              You'll know in 2 hours.
            </span>
          </motion.h1>

          {/* Pain-point conversion paragraph — one clean idea, not bullet
              points. Reads as a single sentence with cadence as a follow-up
              clause. The 3-beat fragmented version visually fell apart
              under any width — kept tripping on its own rhythm. */}
          <motion.div
            initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE, delay: 0.14 }}
            style={{ maxWidth: 640, margin: '0 auto 28px', textAlign: 'center' }}
          >
            <p style={{
              fontSize: 'clamp(15px, 1.7vw, 18px)', color: T2,
              margin: 0, lineHeight: 1.65,
            }}>
              Track every competitor that matters to you across
              {' '}<strong style={{ color: T1 }}>pricing</strong>,
              {' '}<strong style={{ color: T1 }}>hiring</strong>,
              {' '}<strong style={{ color: T1 }}>customer reviews</strong>,
              {' '}<strong style={{ color: T1 }}>news</strong>, and
              {' '}<strong style={{ color: T1 }}>exec moves</strong>.
              Pricing changes alert you within 2 hours;
              your morning brief and dashboard cover the rest.
            </p>
          </motion.div>

          {/* Single primary CTA — kills the "Sign in" duplicate that was competing
              with the primary action. Sign-in still lives in the top nav. */}
          <motion.div
            initial={shouldAnimate ? { opacity: 0, y: 12 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.22 }}
            className="landing-cta-row"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}
          >
            <ArrowCta to="/register" size="sm">Start free trial</ArrowCta>
          </motion.div>

          <motion.p
            initial={shouldAnimate ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.32 }}
            style={{ fontSize: 12, color: T3, marginTop: 14 }}
          >
            7-day Pro trial · No credit card · Cancel any time
          </motion.p>

          {/* ICP qualifier removed from hero — looked orphaned and didn't earn
              its space. Same message is carried by the explicit "Not for"
              section near the bottom of /sources, plus by the targeted
              ad-audience exclusion list. Keeps the hero clean. */}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ STATS */}
      <FadeIn>
        <section style={{
          borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}`,
          background: C1,
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 28px 40px' }}>
            <div className="landing-stats-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 28 }}>
              {STATS_FALLBACK.map(({ key, suffix, label, fallback }, i) => {
                const micro = microOffsets[key as keyof typeof microOffsets] ?? 0;
                const num = (liveStats ? (liveStats[key as keyof LiveStats] ?? fallback) : fallback) + micro;
                return (
                  <div key={label} style={{
                    textAlign: 'center', padding: '16px 16px 12px',
                    borderRight: i < STATS_FALLBACK.length - 1 ? `1px solid ${BR}` : 'none',
                    position: 'relative',
                  }}>
                    {/* Top accent bar — instrument-panel feel without overwhelming */}
                    <span aria-hidden="true" style={{
                      position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                      width: 24, height: 2, background: A, borderRadius: 2,
                      boxShadow: `0 0 8px ${A}60`,
                    }} />
                    <p style={{
                      fontFamily: "'Space Grotesk',sans-serif", fontSize: 42, fontWeight: 800,
                      color: A, margin: '0 0 6px', letterSpacing: '-0.045em',
                      textShadow: `0 0 30px ${A}25`,
                    }}>
                      <CountUp num={num} suffix={suffix} duration={1800} />
                    </p>
                    <p style={{ fontSize: 11, color: T3, margin: 0, fontWeight: 600,
                      letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</p>
                  </div>
                );
              })}
            </div>
            <p style={{ textAlign: 'center', fontSize: 13, color: T3, margin: 0, letterSpacing: '0.02em' }}>
              Numbers update in real time · Tracked across all active accounts
            </p>
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════════════════════ TICKER */}
      <div style={{ overflow: 'hidden', borderBottom: `1px solid ${BR}`, padding: '13px 0', background: BG }}>
        <div style={{ display: 'flex', transform: `translateX(${-offsetX}px)`, willChange: 'transform' }}>
          {doubled.map((item, i) => (
            <div key={i} style={{
              flexShrink: 0, width: ITEM_W,
              display: 'flex', alignItems: 'center', gap: 10, paddingRight: 40,
            }}>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: A, opacity: 0.4, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: T3, whiteSpace: 'nowrap', fontWeight: 600,
                letterSpacing: '0.07em', textTransform: 'uppercase' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ TRUST / METHODOLOGY
           Focus-group feedback: 5 of 15 respondents (Maja, Johanna ×2, Marcus
           ×2) flagged signal-to-noise skepticism as their #1 objection. This
           section directly addresses it: live source-health number from
           /api/intel/summary, plain-English methodology, link to full /sources
           page for the "is my niche covered" check. Marcus: "give me hard
           data on accuracy" — closest honest answer is showing what we
           actually pull from + how we filter, not invented numbers. */}
      <FadeIn>
        <section style={{ background: BG, borderBottom: `1px solid ${BR}`, padding: '72px 28px' }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: A, margin: '0 0 14px' }}>
                How it works
              </p>
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(24px, 3.5vw, 38px)',
                fontWeight: 800, color: T1, margin: '0 0 14px', letterSpacing: '-0.03em', lineHeight: 1.15,
              }}>
                Signal, not noise.<br />
                <span style={{ color: T2, fontWeight: 700 }}>Here's exactly how.</span>
              </h2>
              {sourceHealth && sourceHealth.sourcesActive != null && (
                <p style={{ fontSize: 13, color: T3, margin: '6px 0 0' }}>
                  <span style={{ color: A, fontWeight: 700 }}>{sourceHealth.sourcesActive}/{sourceHealth.sourcesTotal}</span>
                  {' '}Reddit sources reporting now · {sourceHealth.newToday.toLocaleString()} signals ingested today
                </p>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 32 }}>
              {[
                {
                  title: 'What we pull',
                  body:  '148 Reddit subreddits, Hacker News, Dev.to, Greenhouse / Lever / Ashby / Workable, GitHub trending, Product Hunt, 150+ RSS feeds, and patent filings — refreshed every 2 hours.',
                  cta:   { label: 'Full source list →', to: '/sources' },
                },
                {
                  title: 'How we filter',
                  body:  'Each Reddit subreddit is queried with frustration keywords ("doesn\'t work", "switched from", "wish there was") so we surface real complaints — not generic chatter. News is scored by recency and competitor-name proximity. AI extracts funding and exec moves from raw articles into structured signals.',
                },
                {
                  title: 'What we skip',
                  body:  'Bot accounts. Posts under 2 upvotes. Articles older than 90 days for time-sensitive categories. Pricing-page hashes that match dynamic content (employee counts, year-stamps) instead of real changes. We\'d rather miss a soft signal than ship you noise.',
                },
              ].map(card => (
                <div key={card.title} style={{
                  background: C1, border: `1px solid ${BR}`, borderRadius: 12,
                  padding: '22px 24px',
                }}>
                  <p style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
                    textTransform: 'uppercase', color: A, margin: '0 0 10px',
                  }}>{card.title}</p>
                  <p style={{ fontSize: 14, color: T2, margin: 0, lineHeight: 1.7 }}>
                    {card.body}
                  </p>
                  {card.cta && (
                    <p style={{ margin: '14px 0 0' }}>
                      <a href={card.cta.to} style={{ fontSize: 13, color: A, fontWeight: 600, textDecoration: 'none' }}>
                        {card.cta.label}
                      </a>
                    </p>
                  )}
                </div>
              ))}
            </div>

            <p style={{
              fontSize: 12, color: T3, margin: 0, textAlign: 'center',
              maxWidth: 620, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7,
            }}>
              If your competitors are private, regulated (defense, healthcare IT), or non-Western,
              our coverage will be thinner. <a href="/sources" style={{ color: T2, textDecoration: 'underline' }}>Check sources</a> before
              you sign up.
            </p>
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════════════════════ INTERACTIVE DEMO
           Moved up: now sits right after the trust/methodology section so the
           "here's how it works" pitch lands → user can verify on the same
           screen. Was buried below social proof; nobody scrolled that far. */}
      <FadeIn>
        <section id="try-it" style={{ background: BG, borderTop: `1px solid ${BR}`, padding: '88px 28px 72px' }}>
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: A, margin: '0 0 14px' }}>
                Try it on your competitor
              </p>
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(24px, 3.5vw, 38px)',
                fontWeight: 800, color: T1, margin: '0 0 14px', letterSpacing: '-0.03em', lineHeight: 1.15,
              }}>
                Type a competitor name.<br />
                <span style={{ color: A }}>See real signals in 5 seconds.</span>
              </h2>
              <p style={{ fontSize: 14, color: T2, margin: '0 auto', maxWidth: 560, lineHeight: 1.7 }}>
                Live preview — recent news mentions, pulled right now.
                No signup. No email. Just so you know it actually works.
              </p>
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 10, maxWidth: 580, margin: '0 auto', flexWrap: 'wrap' }}>
              <input
                value={demoInput}
                onChange={e => setDemoInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runDemo(); }}
                placeholder='e.g. Stripe | stripe.com'
                disabled={demoLoading}
                style={{
                  flex: 1, minWidth: 220, padding: '14px 16px',
                  background: C1, border: `1px solid ${BR}`, borderRadius: 10,
                  color: T1, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  transition: 'border-color .15s',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = A; }}
                onBlur ={e => { e.currentTarget.style.borderColor = BR; }}
              />
              <button onClick={runDemo} disabled={demoLoading || !demoInput.trim()}
                style={{
                  padding: '14px 24px', background: A, color: '#000',
                  border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: demoLoading || !demoInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: demoLoading || !demoInput.trim() ? 0.5 : 1,
                  fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'opacity .15s',
                }}>
                {demoLoading ? 'Fetching…' : 'See signals'}
              </button>
            </div>

            <p style={{ fontSize: 11, color: T3, textAlign: 'center', margin: '10px auto 0', maxWidth: 580 }}>
              Type a competitor name or domain. Limit: 10 demo runs per hour per IP.
            </p>

            {/* Results */}
            {demoError && (
              <div style={{
                marginTop: 28, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto',
                padding: '14px 16px', background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10,
                color: '#f87171', fontSize: 13, textAlign: 'center',
              }}>{demoError}</div>
            )}

            {demoResult && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                style={{ marginTop: 32 }}>
                <div style={{ background: C1, border: `1px solid ${BR}`, borderRadius: 12, overflow: 'hidden' }}>
                  {/* Header strip */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 20px', borderBottom: `1px solid ${BR}`, background: '#0a0a0a',
                  }}>
                    <div>
                      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T3, margin: '0 0 3px' }}>
                        Live signals for
                      </p>
                      <p style={{ fontSize: 16, fontWeight: 700, color: T1, margin: 0 }}>
                        {demoResult.competitor}
                      </p>
                    </div>
                    {/* Trustpilot rating block removed — pipeline blocked by
                        Cloudflare bot protection. Demo now shows news only. */}
                  </div>

                  {/* News list */}
                  <div>
                    {demoResult.news.length === 0 ? (
                      <p style={{ padding: '22px 20px', color: T3, fontSize: 13, margin: 0, textAlign: 'center' }}>
                        No recent news mentions found.
                      </p>
                    ) : (
                      demoResult.news.map((item, i) => (
                        <a key={i} href={item.url} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'block', padding: '12px 20px',
                            borderBottom: i < demoResult.news.length - 1 ? `1px solid ${BR}` : 'none',
                            textDecoration: 'none', color: 'inherit', transition: 'background .1s',
                          }}
                          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = '#141414'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}>
                          <div style={{ fontSize: 13.5, color: T1, lineHeight: 1.45, fontWeight: 500, marginBottom: 4 }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: 11, color: T3 }}>
                            {item.source}{item.publishedAt ? ' · ' + new Date(item.publishedAt).toLocaleDateString() : ''}
                          </div>
                        </a>
                      ))
                    )}
                  </div>
                </div>

                {/* Conversion footer */}
                <div style={{
                  marginTop: 22, padding: '20px 22px', textAlign: 'center',
                  background: `linear-gradient(180deg, ${A}08, transparent)`,
                  border: `1px solid ${A}30`, borderRadius: 12,
                }}>
                  <p style={{ fontSize: 14, color: T1, margin: '0 0 14px', fontWeight: 600 }}>
                    That's a single fetch. Paying users get this for every tracked competitor —
                    plus pricing-change alerts, new-hire signals, customer complaints, exec moves.
                  </p>
                  <Link to="/register" style={{
                    display: 'inline-block', padding: '10px 20px',
                    background: A, color: '#000', borderRadius: 8,
                    fontSize: 13, fontWeight: 700, textDecoration: 'none',
                  }}>
                    Start free trial →
                  </Link>
                  <p style={{ fontSize: 11, color: T3, margin: '10px 0 0' }}>
                    7-day Pro trial · No credit card
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════════════════════ FEATURES */}
      <section id="features" style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 28px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 80 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
              INTELLIGENCE SUITE
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(28px, 4vw, 48px)',
              fontWeight: 800, color: T1, margin: '0 0 18px', letterSpacing: '-0.035em', lineHeight: 1.08,
            }}>
              One tool. Every signal<br />that moves your market.
            </h2>
            <p style={{ fontSize: 16, color: T2, maxWidth: 460, margin: '0 auto', lineHeight: 1.65 }}>
              Stop switching between LinkedIn, GitHub, Reddit, and Google Alerts.
              Electa Labs filters out the noise and surfaces what actually matters.
            </p>
          </div>
        </FadeIn>

        <div className="landing-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
          {FEATURES.map(({ Icon, title, desc }, i) => (
            <FadeIn key={title} delay={i * 0.06}>
              <div style={{
                background: C1, border: `1px solid ${BR}`,
                borderRadius: 16, padding: '30px',
                transition: 'border-color .25s, box-shadow .25s',
                height: '100%', boxSizing: 'border-box',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = `${A}30`;
                el.style.boxShadow = `0 0 50px ${A}07`;
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = BR;
                el.style.boxShadow = 'none';
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: `${A}0e`, border: `1px solid ${A}1e`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20,
                }}>
                  <Icon />
                </div>
                <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15,
                  fontWeight: 700, color: T1, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
                  {title}
                </h3>
                <p style={{ fontSize: 13, color: T2, margin: 0, lineHeight: 1.72 }}>{desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ PRODUCT PREVIEW */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 28px 80px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
              SEE IT IN ACTION
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(26px, 4vw, 44px)',
              fontWeight: 800, color: T1, margin: '0 0 26px', letterSpacing: '-0.035em',
            }}>
              From URL to full competitive playbook<br />in under 60 seconds.
            </h2>

            {/* 4-step process flow — moved here from the hero where it was
                pushing the CTA below the fold. Pairs naturally with the
                "From URL to full playbook" headline above: shows the actual
                pipeline instead of describing it in prose. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 0, flexWrap: 'wrap', marginTop: 12,
            }}>
              {([
                {
                  step: 'Paste your URL',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.71" />
                    </svg>
                  ),
                },
                {
                  step: 'We map your market',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  ),
                },
                {
                  step: 'See every gap',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ),
                },
                {
                  step: 'Know what to build',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="4" />
                      <line x1="12" y1="2"  x2="12" y2="5"  />
                      <line x1="12" y1="19" x2="12" y2="22" />
                      <line x1="4.93" y1="4.93" x2="7.05" y2="7.05" />
                      <line x1="16.95" y1="16.95" x2="19.07" y2="19.07" />
                      <line x1="2"  y1="12" x2="5"  y2="12" />
                      <line x1="19" y1="12" x2="22" y2="12" />
                      <line x1="4.93" y1="19.07" x2="7.05" y2="16.95" />
                      <line x1="16.95" y1="7.05"  x2="19.07" y2="4.93" />
                    </svg>
                  ),
                },
              ] as { step: string; icon: React.ReactNode }[]).map(({ step, icon }, i) => (
                <React.Fragment key={step}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '8px 14px',
                  }}>
                    {icon}
                    <span style={{ fontSize: 14, color: T1, fontWeight: 600, whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>{step}</span>
                  </div>
                  {i < 3 && (
                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.45 }}>
                      <path d="M1 5h13M10 1l4 4-4 4" stroke={T3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          {/* Mock UI preview */}
          <div style={{
            background: '#0d0d0d', border: `1px solid ${BR}`,
            borderRadius: 16, overflow: 'hidden',
            boxShadow: `0 0 100px ${A}08, 0 40px 80px rgba(0,0,0,0.6)`,
          }}>
            {/* Window chrome */}
            <div style={{
              background: '#111', borderBottom: `1px solid ${BR}`,
              padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
              <span style={{ flex: 1, textAlign: 'center', fontSize: 11, color: '#555', fontFamily: 'monospace' }}>
                electalabs.com — Intelligence Hub
              </span>
            </div>

            <div style={{ padding: '24px 26px 28px' }}>

              {/* ── Header row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: T1, fontFamily: "'Space Grotesk',sans-serif" }}>
                    Competitive Analysis
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: '#555' }}>
                    Crayon · Klue · Meltwater · <span style={{ color: '#444' }}>+4 more</span>
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, pointerEvents: 'none' }}>
                  {['Re-run', 'Export PDF'].map((label, i) => (
                    <span key={label} style={{
                      fontSize: 10, fontWeight: 600, padding: '5px 10px', borderRadius: 6,
                      border: `1px solid ${i === 0 ? A + '40' : '#2a2a2a'}`,
                      color: i === 0 ? A : '#555',
                      background: i === 0 ? `${A}08` : 'transparent',
                    }}>{label}</span>
                  ))}
                </div>
              </div>

              {/* ── Section: What competitors emphasise */}
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                color: '#555', margin: '0 0 8px' }}>
                What competitors emphasise that you don't
              </p>
              <p style={{ fontSize: 12, color: '#777', margin: '0 0 10px' }}>
                Based on scanning competitor websites — use this to sharpen your positioning.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
                {[
                  'Crayon & Klue lead every sales call with battlecards — your homepage never once mentions helping sales teams win deals.',
                  'Meltwater converts enterprise buyers using compliance and SOC 2 messaging prominently. Your site has zero trust signals for procurement teams.',
                  'Every top competitor has a dedicated "vs. [competitor]" page. You\'re invisible to the 60% of buyers who comparison-search before buying.',
                ].map((text, i) => (
                  <div key={i} style={{
                    padding: '11px 14px', fontSize: 14, color: '#aaa', lineHeight: 1.65,
                    background: `${A}05`, border: `1px solid ${A}12`, borderRadius: 8,
                  }}>{text}</div>
                ))}
              </div>

              {/* ── Section: Gaps in your offer */}
              <div style={{
                padding: '14px 16px', marginBottom: 18,
                background: 'rgba(239,100,72,0.06)', border: '1px solid rgba(239,100,72,0.18)', borderRadius: 10,
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: '#e8806a', margin: '0 0 10px' }}>Gaps in your offer</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {[
                    'No sales enablement story — the highest-ROI use case for competitive intel, and none of your copy addresses it.',
                    'Zero social proof from real users. Owler shows customer logos and case studies above the fold. You show none.',
                    'Your pricing page doesn\'t acknowledge competitors exist. Buyers already know they do — silence reads as weakness.',
                  ].map((text, i) => (
                    <p key={i} style={{ margin: 0, fontSize: 14, color: '#aaa', lineHeight: 1.7 }}>{text}</p>
                  ))}
                </div>
              </div>

              {/* ── Section: Competitor cards */}
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                color: '#555', margin: '0 0 8px' }}>What each competitor focuses on</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 20 }}>
                {[
                  { name: 'Crayon', desc: 'Battlecard-first positioning aimed at sales teams. Every page is built around closing deals faster — not just tracking competitors.' },
                  { name: 'Klue', desc: 'AI-curated competitive intel delivered straight into Salesforce and Slack. Sales reps use it live on calls without ever opening a dashboard.' },
                  { name: 'Meltwater', desc: 'Enterprise-grade media monitoring with SOC 2 compliance and dedicated account managers. Targets procurement-heavy orgs with $50K+ budgets.' },
                ].map(({ name, desc }) => (
                  <div key={name} style={{
                    display: 'flex', alignItems: 'baseline', gap: 10,
                    padding: '10px 12px', background: `${A}05`,
                    border: `1px solid ${A}14`, borderRadius: 8,
                  }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: A,
                      background: `${A}12`, border: `1px solid ${A}25`,
                      borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>{name}</span>
                    <p style={{ margin: 0, fontSize: 14, color: '#888', lineHeight: 1.65 }}>{desc}</p>
                  </div>
                ))}
              </div>

              {/* ── Section: Action Plan */}
              <div style={{ borderTop: `1px solid #1a1a1a`, paddingTop: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: T1, fontFamily: "'Space Grotesk',sans-serif" }}>Your Action Plan</p>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '5px 12px', borderRadius: 6,
                    background: A, color: '#000', pointerEvents: 'none',
                  }}>Regenerate</span>
                </div>

                {/* Quick Wins */}
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: '#f0a500', margin: '0 0 8px' }}>Quick Wins · Next 2 Weeks</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                  {[
                    'Ship a "Why not Crayon?" page this week. Buyers googling alternatives will land on you instead of your competitor.',
                    'Add 3 customer logos above the fold on your homepage. Owler converts 2× better with social proof — you have none.',
                    'Rewrite your pricing page to acknowledge Meltwater directly. "Enterprise power at 1/10th the cost" is a headline that closes deals.',
                  ].map((tip, i) => (
                    <div key={i} style={{
                      padding: '9px 12px',
                      background: 'rgba(240,165,0,0.04)', border: '1px solid rgba(240,165,0,0.12)', borderRadius: 8,
                    }}>
                      <span style={{ fontSize: 14, color: '#999', lineHeight: 1.7 }}>{tip}</span>
                    </div>
                  ))}
                </div>

                {/* Strategic Focus */}
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: A, margin: '0 0 8px' }}>Strategic Focus · 1–3 Months</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    'Build a Slack integration that delivers battlecard updates before sales calls. Klue charges $30K/yr for this — you can own it at your price point.',
                    'Create a "Competitor Intel Weekly" email digest. Crayon built their entire PLG loop on this. It\'s free distribution they\'re milking and you\'re not.',
                    'Add a public competitor comparison hub with 20+ tool vs. pages. This single SEO move drives 40% of Klue\'s inbound. You\'re leaving it on the table.',
                  ].map((tip, i) => (
                    <div key={i} style={{
                      padding: '9px 12px',
                      background: `${A}04`, border: `1px solid ${A}12`, borderRadius: 8,
                    }}>
                      <span style={{ fontSize: 14, color: '#999', lineHeight: 1.7 }}>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </FadeIn>
      </section>

      {/* ══════════════════════════════════════════════════════════ REPLACE STACK */}
      <FadeIn>
        <section style={{
          background: BG, padding: '0 28px 80px',
        }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <FadeIn>
              <div style={{ textAlign: 'center', marginBottom: 56 }}>
                <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
                  ONE TAB REPLACES ALL OF THESE
                </p>
                <h2 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(24px, 3.5vw, 40px)',
                  fontWeight: 800, color: T1, margin: '0 0 14px', letterSpacing: '-0.035em',
                }}>
                  Stop stitching five tools together.
                </h2>
                <p style={{ fontSize: 15, color: T2, margin: 0 }}>
                  Everything your competitors are using separately — unified, automated, and free to start.
                </p>
              </div>
            </FadeIn>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 3 }}>
              {[
                { old: 'Google Alerts',          oldDesc: 'Misses most changes, no context',      new: 'Real-time keyword alerts'              },
                { old: 'Manual LinkedIn stalking', oldDesc: 'Hours lost, no structure, easy to miss', new: 'Exec moves & hiring intel'         },
                { old: 'Manual GitHub watching',  oldDesc: 'Time-consuming, easy to miss signals', new: 'Developer momentum, automated'        },
                { old: 'Reddit scrolling',        oldDesc: 'Hours lost, no structure',             new: 'Social mentions, aggregated'          },
                { old: 'Pricing spreadsheets',    oldDesc: 'Always outdated, manually updated',    new: 'Live pricing monitor'                 },
                { old: 'Scattered news tabs',      oldDesc: 'Context-switching, easy to miss', new: 'Funding rounds, automated'            },
              ].map(({ old: oldTool, oldDesc, new: newFeature }) => (
                <FadeIn key={oldTool}>
                  <div style={{
                    background: BG, border: `1px solid ${BR}`,
                    borderRadius: 14, padding: '22px 24px',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}>
                    {/* Old tool */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{
                        minWidth: 18, height: 18, borderRadius: 4,
                        background: 'rgba(255,80,80,0.12)', color: '#ff6060',
                        fontSize: 11, fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>✕</span>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#777', margin: '0 0 2px', textDecoration: 'line-through' }}>{oldTool}</p>
                        <p style={{ fontSize: 11, color: '#555', margin: 0 }}>{oldDesc}</p>
                      </div>
                    </div>
                    {/* New feature */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        minWidth: 18, height: 18, borderRadius: 4,
                        background: `rgba(78,222,163,0.15)`, color: A,
                        fontSize: 11, fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>✓</span>
                      <p style={{ fontSize: 13, fontWeight: 700, color: T1, margin: 0 }}>{newFeature}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>

            <FadeIn delay={0.2}>
              <p style={{ textAlign: 'center', fontSize: 13, color: T3, marginTop: 32 }}>
                All of the above included in the free plan or Pro trial. No integrations required.
              </p>
            </FadeIn>
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════════════════════ MID CTA */}
      <FadeIn>
        <section style={{
          padding: '80px 28px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          background: BG,
        }}>
          {/* green radial glow from center-top */}
          <div style={{
            position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
            width: '120%', height: '120%', pointerEvents: 'none',
            background: `radial-gradient(ellipse at 50% 30%, ${A}20 0%, ${A}08 35%, transparent 65%)`,
          }} />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <p style={{ fontSize: 18, fontWeight: 700, color: T1, margin: '0 0 8px' }}>
              Ready to see who's competing for your customers?
            </p>
            <p style={{ fontSize: 14, color: T2, margin: '0 0 28px' }}>
              7-day Pro trial. No credit card. Takes 30 seconds.
            </p>
            <ArrowCta to="/register" size="sm">Start free trial</ArrowCta>
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════ WHO IT'S FOR + HOW IT WORKS */}
      <section id="about" style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 28px 0' }}>

        {/* WHO IT'S FOR header + cards */}
        <FadeIn>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 40, textAlign: 'center' }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: 0 }}>
              WHO IT'S FOR
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(28px, 4vw, 48px)',
              fontWeight: 800, color: T1, margin: 0, letterSpacing: '-0.035em', lineHeight: 1.08,
            }}>
              Built for founders who refuse to be blindsided.
            </h2>
          </div>
        </FadeIn>

        <div className="landing-personas-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 10, marginBottom: 80 }}>
          {[
            { tag: 'FOUNDERS', featured: true, headline: 'Know your market before the first investor meeting.', body: 'Walk in with data on who\'s funded, what customers are complaining about, and where the gap actually is. No research rabbit holes required.' },
            { tag: 'PRODUCT TEAMS', featured: false, headline: 'Build what customers can\'t find anywhere else.', body: 'Scan competitor features and pricing automatically. Spot what they\'re all ignoring — that\'s where the moat is.' },
          ].map(({ tag, featured, headline, body }, i) => (
            <FadeIn key={tag} delay={i * 0.1}>
              <div style={{
                background: featured ? `${A}08` : C1,
                border: featured ? `1px solid ${A}30` : `1px solid ${BR}`,
                borderRadius: 16, padding: '34px 30px',
                transition: 'border-color .2s, background .2s',
                height: '100%', boxSizing: 'border-box',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = featured ? `${A}55` : BR2;
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLDivElement;
                el.style.borderColor = featured ? `${A}30` : BR;
              }}>
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
                  color: A, margin: '0 0 20px', textTransform: 'uppercase' }}>{tag}</p>
                <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 19,
                  fontWeight: 700, color: T1, margin: '0 0 14px', lineHeight: 1.3,
                  letterSpacing: '-0.02em' }}>
                  {headline}
                </h3>
                <p style={{ fontSize: 13, color: T2, margin: 0, lineHeight: 1.78 }}>{body}</p>
              </div>
            </FadeIn>
          ))}
        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════ HOW IT WORKS */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 28px 80px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 80 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
              HOW IT WORKS
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(26px, 4vw, 44px)',
              fontWeight: 800, color: T1, margin: '0 0 16px', letterSpacing: '-0.035em',
            }}>
              Up and running in 30 seconds.
            </h2>
            <p style={{ fontSize: 16, color: T2, margin: 0, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.65 }}>
              No setup. No integrations. No learning curve.
            </p>
          </div>
        </FadeIn>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3, position: 'relative' }}>
          {[
            {
              step: '01',
              title: 'Describe your product',
              body: 'Enter your website URL or a quick description of what you build. That\'s it — no integrations, no OAuth, no credit card.',
              icon: (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="14" height="14" rx="3" />
                  <line x1="7" y1="7" x2="13" y2="7" />
                  <line x1="7" y1="10" x2="11" y2="10" />
                </svg>
              ),
            },
            {
              step: '02',
              title: 'We find your competitors',
              body: 'Electa Labs maps who\'s competing for your customers, what they charge, what they\'re building, and where they\'re falling short.',
              icon: (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="9" r="6" />
                  <line x1="13.5" y1="13.5" x2="17" y2="17" />
                  <line x1="9" y1="6" x2="9" y2="12" />
                  <line x1="6" y1="9" x2="12" y2="9" />
                </svg>
              ),
            },
            {
              step: '03',
              title: 'Get your edge',
              body: 'Live alerts the moment something changes. A step-by-step build roadmap. Pricing gaps, pain points, hiring signals — all in one place.',
              icon: (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={A} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4,10 8,14 16,6" />
                </svg>
              ),
            },
          ].map(({ step, title, body, icon }, i) => (
            <FadeIn key={step} delay={i * 0.1}>
              <div style={{
                background: C1, border: `1px solid ${BR}`,
                borderRadius: 16, padding: '36px 32px',
                position: 'relative', height: '100%', boxSizing: 'border-box',
              }}>
                {/* Step number — faint background */}
                <span style={{
                  position: 'absolute', top: 28, right: 28,
                  fontFamily: "'Space Grotesk',sans-serif",
                  fontSize: 48, fontWeight: 800, color: `${A}08`,
                  lineHeight: 1, userSelect: 'none',
                }}>{step}</span>

                {/* Icon */}
                <div style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: `${A}0e`, border: `1px solid ${A}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 24,
                }}>
                  {icon}
                </div>

                <h3 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 17,
                  fontWeight: 700, color: T1, margin: '0 0 12px', letterSpacing: '-0.02em',
                }}>{title}</h3>
                <p style={{ fontSize: 13, color: T2, margin: 0, lineHeight: 1.75 }}>{body}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* CTA nudge */}
        <FadeIn delay={0.3}>
          <div style={{ textAlign: 'center', marginTop: 56 }}>
            <ArrowCta to="/register" size="sm">Start free trial</ArrowCta>
            <p style={{ fontSize: 12, color: T3, marginTop: 14 }}>7-day Pro trial · No credit card</p>
          </div>
        </FadeIn>
      </section>

      {/* ══════════════════════════════════════════════════════════ SOCIAL PROOF
          Two real "love"-rated testimonials submitted through the in-app
          feedback form, paired with the industry-chip block as supporting
          context. Replaces the old generic-platform-logo block. */}
      <FadeIn>
        <section style={{
          borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}`,
          padding: '88px 28px', position: 'relative', overflow: 'hidden',
          background: BG,
        }}>
          <AmbientGlow />
          <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: A, margin: '0 0 14px' }}>
              What founders are saying
            </p>
            <h3 style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 'clamp(22px, 2.6vw, 30px)',
              fontWeight: 700, color: T1, letterSpacing: '-0.025em',
              margin: '0 0 48px', lineHeight: 1.25,
            }}>
              Early users, real reactions.
            </h3>

            {/* Testimonial card grid — 2 cards desktop, 1-column mobile */}
            <div className="landing-testimonials" style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16, maxWidth: 880, margin: '0 auto 48px',
            }}>
              {[
                {
                  quote: "I've been checking out electalabs.com, and honestly, I'm impressed.",
                  name: 'Rauf Ridwan',
                  role: 'Digital Marketing Specialist',
                },
                {
                  quote: "The strongest part is the 'turning AI into measurable operational and growth outcomes' angle behind it.",
                  name: 'David Morgan',
                  role: 'Client Acquisition Specialist',
                },
              ].map(({ quote, name, role }) => (
                <figure key={name} style={{
                  background: C1, border: `1px solid ${BR}`,
                  borderRadius: 14, padding: '28px 26px 24px',
                  margin: 0, textAlign: 'left', position: 'relative',
                  display: 'flex', flexDirection: 'column', gap: 18,
                  transition: 'border-color .25s, box-shadow .25s',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${A}35`;
                  el.style.boxShadow = `0 0 40px ${A}08`;
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = BR;
                  el.style.boxShadow = 'none';
                }}>
                  {/* Decorative open-quote glyph — keeps the card from feeling like
                      a list item, signals "this is a testimonial" instantly. */}
                  <span aria-hidden="true" style={{
                    position: 'absolute', top: 14, right: 18,
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontSize: 56, fontWeight: 800, color: `${A}18`,
                    lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
                  }}>"</span>
                  {/* Rating — small green heart row */}
                  <div style={{ display: 'flex', gap: 3, color: A }} aria-label="Rated love">
                    {[0,1,2,3,4].map(i => (
                      <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill={A} aria-hidden="true">
                        <path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z"/>
                      </svg>
                    ))}
                  </div>
                  <blockquote style={{
                    margin: 0, fontSize: 15, lineHeight: 1.6, color: T1,
                    fontWeight: 500, letterSpacing: '-0.005em',
                  }}>{quote}</blockquote>
                  <figcaption style={{
                    paddingTop: 14, borderTop: `1px solid ${BR}`,
                    fontSize: 13, color: T2, fontWeight: 600,
                  }}>
                    {name}
                    <span style={{ color: T3, fontWeight: 400 }}> · {role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>

            {/* Industry context — supports the testimonials as the breadth proof */}
            <p style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: T3, margin: '0 0 16px',
            }}>
              And founders across
            </p>
            <div style={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
              gap: 10, maxWidth: 760, margin: '0 auto',
            }}>
              {[
                'B2B SaaS', 'AI / ML', 'Fintech', 'Climate', 'Dev Tools',
                'Marketing Tech', 'E-commerce',
              ].map(industry => (
                <span key={industry} style={{
                  fontSize: 13, fontWeight: 600, color: T1,
                  background: `${A}10`, border: `1px solid ${A}30`,
                  padding: '8px 16px', borderRadius: 100,
                  letterSpacing: '-0.005em',
                }}>{industry}</span>
              ))}
            </div>
          </div>
        </section>
      </FadeIn>

      {/* ══════════════════════════════════════════════════════════ PRICING */}
      <section id="pricing" style={{ maxWidth: 1240, margin: '0 auto', padding: '96px 28px 60px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
              PRICING
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 800, color: T1, margin: '0 0 14px', letterSpacing: '-0.035em',
            }}>
              Simple, transparent pricing.
            </h2>
            <p style={{ fontSize: 15, color: T2, margin: 0 }}>
              Start free. Upgrade when you're ready for full competitive intelligence.
            </p>
          </div>
        </FadeIn>

        {/* Monthly / Annual toggle — only meaningful for Pro right now.
            Sits above the grid so users can pre-select cadence before
            comparing tiers. Annual saves $120/year on Pro. */}
        <FadeIn delay={0.04}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: 4, background: C1, border: `1px solid ${BR}`, borderRadius: 100,
            }}>
              {(['monthly', 'annual'] as const).map(cycle => {
                const active = billingCycle === cycle;
                return (
                  <button
                    key={cycle}
                    onClick={() => setBillingCycle(cycle)}
                    style={{
                      padding: '8px 18px', borderRadius: 100, border: 'none',
                      background: active ? A : 'transparent',
                      color:      active ? '#000' : T2,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      fontFamily: 'inherit', transition: 'background .15s, color .15s',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    {cycle === 'monthly' ? 'Monthly' : 'Annual'}
                    {cycle === 'annual' && (
                      <span style={{
                        fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
                        padding: '2px 7px', borderRadius: 100,
                        background: active ? '#000' : `${A}20`,
                        color:      active ? A      : A,
                      }}>SAVE 20%</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </FadeIn>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 12, alignItems: 'start',
        }}>
          {/* Free */}
          <FadeIn delay={0}>
            <div className="landing-pricing-card" style={{
              background: C1, border: `1px solid ${BR}`,
              borderRadius: 16, padding: '28px 26px',
              display: 'flex', flexDirection: 'column',
            }}>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: T3, margin: '0 0 10px' }}>Free</p>
              {/* minHeight 60 reserves space matching Pro's price + annual subline,
                  so the divider + button + feature list below line up across all
                  four cards. Free has no annual line — the bottom 20px stay empty. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16, minHeight: 60 }}>
                <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 40,
                  fontWeight: 800, color: T1, letterSpacing: '-0.03em', lineHeight: 1 }}>$0</span>
                <span style={{ fontSize: 13, color: T3 }}>forever</span>
              </div>
              <p style={{ fontSize: 13, color: T2, margin: '0 0 24px', lineHeight: 1.6, minHeight: 42 }}>
                Get a feel for your competitive landscape, no card needed.
              </p>
              <Link to="/register" style={{
                display: 'block', padding: '11px 0', textAlign: 'center',
                border: `1px solid ${BR}`, borderRadius: 9,
                fontSize: 13, fontWeight: 700, color: T3,
                textDecoration: 'none', marginBottom: 24, transition: 'border-color .15s, color .15s',
              }}
              onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = '#333'; el.style.color = T2; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = BR; el.style.color = T3; }}>
                Start free
              </Link>
              <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {[
                  { text: 'Trend Radar', ok: true },
                  { text: 'News feed (10 articles/day)', ok: true },
                  { text: '1 keyword (no alerts)', ok: true },
                  { text: 'Weekly digest newsletter', ok: true },
                  { text: 'Slack integration', ok: false },
                  { text: 'Daily personalised brief', ok: false },
                  { text: 'Intelligence Hub', ok: false },
                  { text: 'Market Advisor', ok: false },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {f.ok
                      ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}><path d="M2.5 7l3 3 6-6" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : <span style={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T3, flexShrink: 0 }}>—</span>
                    }
                    <span style={{ fontSize: 13, color: f.ok ? T1 : T3 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Essential — sits between Free and Pro for solo founders who want
              the headline value (daily brief + pricing alerts) on a tighter
              budget. Backend caps at 3 tracked competitors + 3 pricing
              monitors. No Slack, no real-time keyword alerts, no Market
              Advisor — those are deliberate Pro carrots. */}
          <FadeIn delay={0.04}>
            <div className="landing-pricing-card" style={{
              background: C1, border: `1px solid ${BR}`,
              borderRadius: 16, padding: '28px 26px',
              display: 'flex', flexDirection: 'column',
            }}>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: T2, margin: '0 0 10px' }}>Essential</p>
              {/* minHeight 60 — see Free card comment. Essential has no annual line. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16, minHeight: 60 }}>
                <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 40,
                  fontWeight: 800, color: T1, letterSpacing: '-0.03em', lineHeight: 1 }}>$19</span>
                <span style={{ fontSize: 13, color: T3 }}>/month</span>
              </div>
              <p style={{ fontSize: 13, color: T2, margin: '0 0 24px', lineHeight: 1.6, minHeight: 42 }}>
                Daily brief + pricing alerts on 3 competitors. For solo founders.
              </p>
              <Link to="/register" style={{
                display: 'block', padding: '11px 0', textAlign: 'center',
                background: 'transparent', border: `1px solid ${T2}`, borderRadius: 9,
                fontSize: 13, fontWeight: 700, color: T1,
                textDecoration: 'none', marginBottom: 24, transition: 'background .15s, border-color .15s',
              }}
              onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = `${A}10`; el.style.borderColor = A; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = 'transparent'; el.style.borderColor = T2; }}>
                Start 7-day free trial
              </Link>
              <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {[
                  { text: 'Track 3 competitors',                  ok: true  },
                  { text: 'Pricing-page alerts (3 monitors)',     ok: true,  highlight: true },
                  { text: 'Daily personalised brief',             ok: true  },
                  { text: 'Watchlist (daily digest)',             ok: true  },
                  { text: 'Intelligence Hub — full access',       ok: true  },
                  { text: 'Real-time keyword alerts',             ok: false },
                  { text: 'Slack integration',                    ok: false },
                  { text: 'Market Advisor',                       ok: false },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {f.ok
                      ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}><path d="M2.5 7l3 3 6-6" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : <span style={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T3, flexShrink: 0 }}>—</span>
                    }
                    <span style={{ fontSize: 13, color: f.ok ? (f.highlight ? '#fff' : T1) : T3, fontWeight: f.highlight ? 700 : 400 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Pro — stronger visual dominance via brighter border, glow shadow,
              radial accent background, and slightly elevated badge.
              Skill rule: primary-action + elevation-consistent. */}
          <FadeIn delay={0.08}>
            <div className="landing-pricing-card landing-pricing-pro" style={{
              background: '#0d1a14', border: `1px solid ${A}55`,
              borderRadius: 16, padding: '32px 26px 28px',
              display: 'flex', flexDirection: 'column',
              position: 'relative',
              boxShadow: `0 0 0 1px ${A}10 inset, 0 12px 40px ${A}12, 0 0 80px ${A}08`,
              transform: 'translateY(-4px)',
            }}>
              {/* Radial accent — pulls the eye toward Pro without being loud */}
              <div aria-hidden="true" style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '60%',
                background: `radial-gradient(ellipse at 50% 0%, ${A}14 0%, transparent 60%)`,
                pointerEvents: 'none', borderRadius: '16px 16px 0 0',
              }} />
              <div style={{
                position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                background: A, color: '#000', fontSize: 10, fontWeight: 800,
                padding: '4px 14px', borderRadius: 100, letterSpacing: '0.09em', whiteSpace: 'nowrap',
                boxShadow: `0 4px 14px ${A}40`,
              }}>MOST POPULAR</div>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: A, margin: '0 0 10px' }}>Pro</p>
              {/* Column-stacked: price row on top, annual subline below. minHeight 60
                  matches the empty reservation on Free/Essential/Scale so the
                  divider/button below align across the row. Previously this was a
                  flex-with-wrap layout that pushed the divider ~20px lower on Pro
                  when the annual badge wrapped onto a second line. */}
              <div style={{ marginBottom: 16, minHeight: 60 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 40,
                    fontWeight: 800, color: T1, letterSpacing: '-0.03em', lineHeight: 1 }}>
                    ${billingCycle === 'annual' ? '55' : '69'}
                  </span>
                  <span style={{ fontSize: 13, color: T3 }}>/month</span>
                </div>
                <div style={{ fontSize: 11, color: A, marginTop: 6 }}>
                  {billingCycle === 'annual'
                    ? '$660 billed yearly'
                    : '$55/mo billed annually'}
                </div>
              </div>
              <p style={{ fontSize: 13, color: T2, margin: '0 0 24px', lineHeight: 1.6, minHeight: 42 }}>
                Full competitive intelligence delivered to Slack.
              </p>
              <Link to="/register" style={{
                display: 'block', padding: '11px 0', textAlign: 'center',
                background: A, borderRadius: 9,
                fontSize: 13, fontWeight: 800, color: '#000',
                textDecoration: 'none', marginBottom: 24, transition: 'opacity .15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.85'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}>
                Start 7-day free trial
              </Link>
              <div style={{ borderTop: `1px solid ${A}20`, paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {[
                  { text: 'Everything in Free' },
                  { text: 'Slack integration — real-time alerts + daily digest', highlight: true },
                  { text: 'Per-signal-type channel routing' },
                  { text: 'Intelligence Hub — funding, hiring, exec moves, launches' },
                  { text: 'My Tracking — follow any company worldwide' },
                  { text: 'Market Advisor — competitors, gaps & strategy' },
                  { text: 'Watchlist & real-time keyword alerts' },
                  { text: 'Daily personalised brief' },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M2.5 7l3 3 6-6" stroke={A} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <span style={{ fontSize: 13, color: f.highlight ? '#fff' : T1, lineHeight: 1.4, fontWeight: f.highlight ? 700 : 400 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Scale — power-user tier. Renamed from "Teams" because (a) the
              feature was actually single-user with bigger limits, not multi-
              seat collaboration, and (b) the per-seat math was a pricing leak
              ($99/5seats = $19.80 < Pro $69). Now positioned for agencies,
              BD teams, and scale-stage founders tracking entire markets. */}
          <FadeIn delay={0.16}>
            <div className="landing-pricing-card" style={{
              background: C1, border: `1px solid ${BR}`,
              borderRadius: 16, padding: '28px 26px',
              display: 'flex', flexDirection: 'column', opacity: 0.7,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: 16, right: 16,
                background: '#1a1a1a', border: `1px solid ${BR}`,
                color: T3, fontSize: 10, fontWeight: 700,
                padding: '3px 10px', borderRadius: 100, letterSpacing: '0.08em',
              }}>COMING SOON</div>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: T3, margin: '0 0 10px' }}>Scale</p>
              {/* minHeight 60 — see Free card comment. Scale has no annual line. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16, minHeight: 60 }}>
                <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 40,
                  fontWeight: 800, color: T1, letterSpacing: '-0.03em', lineHeight: 1 }}>$299</span>
                <span style={{ fontSize: 13, color: T3 }}>/month</span>
              </div>
              <p style={{ fontSize: 13, color: T2, margin: '0 0 24px', lineHeight: 1.6, minHeight: 42 }}>
                For agencies, BD teams, and founders tracking entire markets.
              </p>
              {/* Spacer matching the button + its bottom margin on Free/Pro,
                  so the divider/features list starts at the same Y across
                  all three cards. */}
              <div aria-hidden="true" style={{ height: 35, marginBottom: 24 }} />
              {/* Feature list — mirrors structure of Free/Pro for symmetry.
                  Dimmer text + grey checks signal "coming soon" without
                  needing a non-functional CTA. */}
              <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {[
                  'Everything in Pro',
                  '50 tracked competitors (vs 20)',
                  '200 pricing monitors (vs 50)',
                  'Unlimited AI Briefs (vs 20/day)',
                  'Priority API access',
                  'Priority support',
                ].map((text, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }}>
                      <path d="M2.5 7l3 3 6-6" stroke={T3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{ fontSize: 13, color: T3, lineHeight: 1.4 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.2}>
          <p style={{ textAlign: 'center', fontSize: 13, color: T3, marginTop: 28 }}>
            7-day Pro trial on all plans · No credit card required · Cancel any time
          </p>
        </FadeIn>
      </section>

      {/* ══════════════════════════════════════════════════════════ FAQ */}
      <section style={{ maxWidth: 660, margin: '0 auto', padding: '40px 28px 80px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px' }}>
              FAQ
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(26px, 4vw, 40px)',
              fontWeight: 800, color: T1, margin: 0, letterSpacing: '-0.035em',
            }}>
              Common questions.
            </h2>
          </div>
        </FadeIn>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {FAQS.map(({ q, a }, i) => (
            <FadeIn key={i} delay={i * 0.04}>
              <div style={{
                border: `1px solid ${activeFaq === i ? BR2 : BR}`,
                borderRadius: 13, overflow: 'hidden',
                background: activeFaq === i ? C1 : 'transparent',
                transition: 'background .2s, border-color .2s',
              }}>
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  style={{
                    width: '100%', background: 'none', border: 'none',
                    padding: '20px 24px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                    textAlign: 'left',
                  }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T1, lineHeight: 1.4 }}>{q}</span>
                  <motion.span
                    animate={{ rotate: activeFaq === i ? 45 : 0 }}
                    transition={{ duration: 0.22 }}
                    style={{ fontSize: 22, color: T3, flexShrink: 0, display: 'block', lineHeight: 1 }}
                  >+</motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {activeFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: EASE }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{ padding: '0 24px 22px' }}>
                        <p style={{ fontSize: 13, color: T2, margin: 0, lineHeight: 1.78 }}>{a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ NEWSLETTER */}
      <section style={{
        borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}`,
        position: 'relative', overflow: 'hidden',
        backgroundImage: 'url(https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        {/* Dark overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,4,4,0.84)', pointerEvents: 'none' }} />
        {/* Green glow from center */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 800, height: 500, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 50% 50%, rgba(78,222,163,0.18) 0%, rgba(78,222,163,0.06) 35%, rgba(78,222,163,0) 65%)',
        }} />
        {/* Top + bottom fade into page */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: 100, pointerEvents: 'none',
          background: `linear-gradient(to top, transparent, ${BG})`,
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 100, pointerEvents: 'none',
          background: `linear-gradient(to bottom, transparent, ${BG})`,
        }} />

        <div style={{ maxWidth: 560, margin: '0 auto', padding: '110px 28px', textAlign: 'center', position: 'relative' }}>
          <FadeIn>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: A, margin: '0 0 18px',
              textShadow: '0 1px 10px rgba(0,0,0,0.9)' }}>
              WEEKLY BRIEFING
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(24px, 4vw, 38px)',
              fontWeight: 800, color: '#ffffff', margin: '0 0 14px', letterSpacing: '-0.035em',
              textShadow: '0 2px 20px rgba(0,0,0,0.9)',
            }}>
              Stay ahead of your market.
            </h2>
            <p style={{ fontSize: 14, color: '#d8d8d8', margin: '0 0 36px', lineHeight: 1.65,
              textShadow: '0 1px 8px rgba(0,0,0,0.95)' }}>
              A weekly digest of the signals that matter in your industry.
              No noise. No spam. Unsubscribe any time.
            </p>

            {newsletterSent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '16px 28px', borderRadius: 11,
                  background: `${A}0d`, border: `1px solid ${A}28`,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: A, boxShadow: `0 0 8px ${A}` }} />
                <span style={{ fontSize: 14, color: A, fontWeight: 600 }}>You're on the list. Talk soon.</span>
              </motion.div>
            ) : (
              <form onSubmit={handleNewsletter}
                style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* Honeypot — hidden from real users, bots fill this in */}
                <input
                  type="text" name="website" autoComplete="off" tabIndex={-1} aria-hidden="true"
                  style={{ position: 'absolute', left: -9999, opacity: 0, pointerEvents: 'none', width: 1, height: 1 }}
                  defaultValue=""
                />
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  required
                  style={{
                    flex: '1 1 220px', maxWidth: 300,
                    padding: '13px 16px', borderRadius: 10,
                    background: BG, border: `1px solid ${BR2}`,
                    color: T1, fontSize: 14, fontFamily: 'inherit',
                    outline: 'none', transition: 'border-color .15s',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = `${A}45`; }}
                  onBlur={e => { e.currentTarget.style.borderColor = BR2; }}
                />
                <button type="submit" style={{
                  padding: '13px 24px', borderRadius: 10,
                  background: A, border: 'none', color: '#000',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'background .15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = AB; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = A; }}>
                  Subscribe
                </button>
              </form>
            )}

            <p style={{ fontSize: 11, color: '#aaaaaa', marginTop: 16,
              textShadow: '0 1px 8px rgba(0,0,0,0.95)' }}>
              Join 500+ founders already subscribed
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ FINAL CTA */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 500,
          background: `radial-gradient(ellipse at 50% 20%, ${A}30 0%, transparent 60%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ borderTop: `1px solid ${BR}` }}>
          <div style={{
            maxWidth: 860, margin: '0 auto',
            padding: '100px 28px 120px', textAlign: 'center', position: 'relative',
          }}>
            <FadeIn>
              <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: A, margin: '0 0 20px' }}>
                GET STARTED FREE
              </p>
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(30px, 5.5vw, 56px)',
                fontWeight: 800, color: T1, margin: '0 0 22px', letterSpacing: '-0.045em',
                lineHeight: 1.06,
              }}>
                Your competitors are being<br />tracked right now.
              </h2>
              <p style={{ fontSize: 17, color: '#c8c8c8', margin: '0 0 48px', lineHeight: 1.7 }}>
                Right now, your competitors may already be watching your market.<br />Start your 7-day Pro trial — full access, no credit card.
              </p>

              <ArrowCta to="/register">Start free trial</ArrowCta>

              <p style={{ fontSize: 12, color: T2, marginTop: 18 }}>
                Takes 30 seconds to set up · Cancel any time
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════ FOOTER */}
      <footer style={{ borderTop: `1px solid ${BR}`, padding: '28px 28px' }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 16,
        }}>
          {/* Left: logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo size={20} />
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 14,
              fontWeight: 700, color: T3 }}>Electa Labs</span>
          </div>

          {/* Center: nav + legal */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Features',         href: '#features' },
              { label: 'Pricing',          href: '/#pricing' },
              { label: 'About',            href: '#about'    },
              { label: 'Login',            href: '/login'    },
              { label: 'Blog',             href: '/blog'     },
              { label: 'Creators',         href: '/creators' },
              { label: 'Privacy Policy',   href: '/privacy'  },
              { label: 'Terms of Service', href: '/terms'    },
            ].map(({ label, href }) => (
              <a key={label} href={href} style={{
                fontSize: 12, color: T3, textDecoration: 'none',
                transition: 'color .15s', fontWeight: 500,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = T2; }}
              onMouseLeave={e => { e.currentTarget.style.color = T3; }}>
                {label}
              </a>
            ))}
          </div>

          {/* Right: copyright */}
          <p style={{ fontSize: 12, color: T3, margin: 0 }}>
            © {new Date().getFullYear()} Electa Labs
          </p>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════ COOKIE BANNER */}
      <AnimatePresence>
        {cookieConsent === null && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            style={{
              position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              zIndex: 999, width: 'calc(100% - 48px)', maxWidth: 680,
              background: '#111', border: `1px solid ${BR2}`,
              borderRadius: 16, padding: '20px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 20, flexWrap: 'wrap',
              boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: T1, margin: '0 0 4px' }}>
                We use cookies
              </p>
              <p style={{ fontSize: 12, color: T3, margin: 0, lineHeight: 1.6 }}>
                We use essential cookies to keep the site working and optional analytics cookies to understand how you use it.{' '}
                <a href="/privacy" style={{ color: A, textDecoration: 'none' }}>Privacy Policy</a>
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={() => {
                  localStorage.setItem('cookie_consent', 'declined');
                  setCookieConsent('declined');
                }}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: `1px solid ${BR2}`,
                  background: 'transparent', color: T2, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .15s, color .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3a3a3a'; e.currentTarget.style.color = T1; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BR2; e.currentTarget.style.color = T2; }}
              >
                Decline
              </button>
              <button
                onClick={() => {
                  localStorage.setItem('cookie_consent', 'accepted');
                  setCookieConsent('accepted');
                }}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: A, color: '#000', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'background .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = AB; }}
                onMouseLeave={e => { e.currentTarget.style.background = A; }}
              >
                Accept all
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default LandingPage;
